import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';

import { NoticeCard } from '@/components/feedback/notice-card';
import { OnboardingShell } from '@/components/onboarding/onboarding-shell';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { usePurchaseIntent, useWorkspaceActivated } from '@/hooks/use-purchase-intent';
import { useRegions } from '@/hooks/use-regions';
import { PRICING_ROUTE } from '@/lib/onboarding-funnel';
import { readPurchaseIntentHint } from '@/lib/purchase-intent-hint';

/**
 * Same bounded schedule as the legacy subscription activation poll
 * (`apps/client/src/hooks/use-subscription.ts`, removed with the legacy funnel,
 * ESCLOUD-334). Copied locally rather than shared because nothing else needs it
 * once that hook is gone.
 */
const POLL = { fast: 2_500, backoff: 10_000, stopped: false } as const;
const POLL_BACKOFF_AFTER_MS = 30_000;
const POLL_STOPS_AFTER_MS = 5 * 60_000;

type TPollPhase = keyof typeof POLL;

/** Missed polls in a row before a transport failure is called out. */
const UNREACHABLE_AFTER_POLLS = 3;

type TWaitState = 'waiting' | 'slow' | 'unreachable' | 'stopped';

/**
 * Copy for every waiting state. None of these is an error state: the webhook
 * is what activates the workspace, this page only watches for it, and a
 * transport failure here says so, in the reader's own words, rather than
 * looking like a payment problem.
 */
const WAIT_COPY: Record<
    TWaitState,
    { title: string; detail: (regionName: string) => string; support: boolean }
> = {
    waiting: {
        title: 'Activating your subscription',
        detail: (regionName) =>
            `Confirming the payment and preparing your workspace in the ${regionName} region. Please keep this page open.`,
        support: false,
    },
    slow: {
        title: 'Still activating',
        detail: () =>
            'This is taking longer than usual. Your payment went through and nothing further is needed ' +
            'from you. We will email you the moment the workspace is ready.',
        support: true,
    },
    unreachable: {
        title: 'Still activating',
        detail: () =>
            'We cannot reach the server to check on your workspace, and keep trying. This is a problem ' +
            'reaching us, not a problem with your payment. It is safe to close this page - we will email ' +
            'you the moment the workspace is ready.',
        support: true,
    },
    stopped: {
        title: 'Still activating',
        detail: () =>
            'Your payment is safe. Activation happens on our side and does not depend on this page, so we ' +
            'have stopped checking from here. We will email you the moment the workspace is ready.',
        support: true,
    },
};

/**
 * Wall-clock from mount rather than a count of polls, so a stretch of failed
 * requests cannot extend the bound: the point is to stop asking, and a poll
 * that keeps failing is the case that most needs to.
 */
function usePollPhase(): TPollPhase {
    const [phase, setPhase] = useState<TPollPhase>('fast');
    useEffect(() => {
        const toBackoff = setTimeout(() => setPhase('backoff'), POLL_BACKOFF_AFTER_MS);
        const toStopped = setTimeout(() => setPhase('stopped'), POLL_STOPS_AFTER_MS);
        return () => {
            clearTimeout(toBackoff);
            clearTimeout(toStopped);
        };
    }, []);
    return phase;
}

/**
 * Post-payment landing of the purchase funnel (ESCLOUD-332). Stripe's return
 * URL points here after a redirect-based authentication, and the payment page
 * navigates here after an in-page confirm; either way the URL activates
 * nothing by itself. The webhook is what materialises the tenant; this page
 * polls the intent until it reads `completed`. A `session_id` query parameter
 * may be present on a 3-D Secure return and is deliberately ignored - the
 * intent is the authority, not the Checkout Session.
 */
export function ActivatingIntentPage() {
    const [searchParams] = useSearchParams();
    const intentId = searchParams.get('intent');
    const hint = useMemo(() => readPurchaseIntentHint(), []);
    const region = hint?.region ?? null;
    const phase = usePollPhase();
    const regions = useRegions();
    const workspaceActivated = useWorkspaceActivated();
    const {
        data: intent,
        error,
        errorUpdatedAt,
        isSuccess,
    } = usePurchaseIntent(region, intentId, {
        refetchInterval: POLL[phase],
    });
    const [missedPolls, setMissedPolls] = useState(0);

    useEffect(() => {
        if (isSuccess) {
            setMissedPolls(0);
            return;
        }
        if (error) setMissedPolls((missed) => missed + 1);
    }, [error, errorUpdatedAt, isSuccess]);

    const activatedTenantId = useRef<string | null>(null);
    const completedTenantId = intent?.status === 'completed' ? intent.tenantId : null;
    useEffect(() => {
        if (!completedTenantId || !region) return;
        if (activatedTenantId.current === completedTenantId) return;
        activatedTenantId.current = completedTenantId;
        void workspaceActivated(completedTenantId, region);
    }, [completedTenantId, region, workspaceActivated]);

    if (!intentId || !region) {
        return <Navigate to={PRICING_ROUTE} replace />;
    }

    if (intent?.status === 'expired') {
        return (
            <OnboardingShell>
                <NoticeCard
                    title="This purchase attempt has expired"
                    body="The payment session was not completed in time and nothing has been charged. You can start again from the plans."
                    action={
                        <Button asChild variant="outline">
                            <Link to={PRICING_ROUTE}>Back to plans</Link>
                        </Button>
                    }
                />
            </OnboardingShell>
        );
    }

    const regionName = regions.data?.items.find((r) => r.code === region)?.name ?? region.toUpperCase();
    const waitState: TWaitState =
        phase === 'stopped'
            ? 'stopped'
            : missedPolls >= UNREACHABLE_AFTER_POLLS
              ? 'unreachable'
              : phase === 'backoff'
                ? 'slow'
                : 'waiting';
    const copy = WAIT_COPY[waitState];

    return (
        <OnboardingShell>
            <div className="mx-auto max-w-[520px] py-16 text-center">
                <Spinner className="mx-auto mb-5 h-7 w-7 text-primary" />
                <h1 className="m-0 mb-2 text-xl font-semibold text-foreground">{copy.title}</h1>
                <p className="m-0 text-sm leading-relaxed text-muted-foreground">{copy.detail(regionName)}</p>
                {copy.support && (
                    <a
                        href="mailto:contact@therenva.com"
                        className="mt-4 inline-block text-[13px] text-primary underline-offset-2 hover:underline"
                    >
                        Contact support
                    </a>
                )}
            </div>
        </OnboardingShell>
    );
}
